import { useState, type FormEvent } from 'react';
import type { SupabaseSource } from '../../data/supabaseSource.ts';

/**
 * Email one-time code sign-in. Codes (not magic links) because a link opens Safari,
 * and an installed iPhone web app keeps its own separate storage from Safari.
 */
export function SignInScreen({ source, onSignedIn }: { source: SupabaseSource; onSignedIn: () => void }) {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const send = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email)) {
      setError('Enter your email address.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await source.sendCode(email.trim().toLowerCase());
      setStage('code');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const verify = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6,8}$/.test(code.trim())) {
      setError('Enter the code from the email.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await source.verifyCode(email.trim().toLowerCase(), code.trim());
      onSignedIn();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="screen" style={{ paddingBottom: 40, justifyContent: 'center', minHeight: '100dvh' }}>
      <div className="stack" style={{ gap: 2 }}>
        <span className="eyebrow">Natangelo</span>
        <h1 className="h1">Sign in</h1>
      </div>
      {stage === 'email' ? (
        <form className="stack-lg" onSubmit={send} noValidate>
          <div className="field">
            <label htmlFor="email">Email</label>
            <input id="email" className="input" type="email" autoComplete="email" inputMode="email" value={email} onChange={(e) => setEmail(e.target.value)} aria-invalid={!!error || undefined} />
          </div>
          {error && (
            <span className="small" role="alert" style={{ color: 'var(--orange-text)' }}>
              {error}
            </span>
          )}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Sending…' : 'Email me a code'}
          </button>
          <span className="small muted">Only Michael and Natalie can sign in.</span>
        </form>
      ) : (
        <form className="stack-lg" onSubmit={verify} noValidate>
          <span className="small muted">We sent a code to {email}.</span>
          <div className="field">
            <label htmlFor="code">Code</label>
            <input id="code" className="input" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value)} aria-invalid={!!error || undefined} />
          </div>
          {error && (
            <span className="small" role="alert" style={{ color: 'var(--orange-text)' }}>
              {error}
            </span>
          )}
          <button type="submit" className="btn btn-primary" disabled={busy}>
            {busy ? 'Checking…' : 'Sign in'}
          </button>
          <button type="button" className="btn" onClick={() => setStage('email')}>
            Use a different email
          </button>
        </form>
      )}
    </div>
  );
}
